"use client";

import React, { useEffect } from "react";
import { useRouter } from "next/navigation";
import { useAuthOrUndefined } from "@shesha-io/reactjs";
import { MEMBER_TOKENS as T } from "@/screens/member/tokens";
import { URL_AUTH_LOGIN, URL_MEMBER_DASHBOARD } from "@/routes";

/**
 * Portal entry point. Replaces the Shesha starter's "Build your first app" landing page — this is a
 * member-facing portal, so the root sends people to their dashboard or to the login card.
 *
 * The decision is client-side by necessity: whether a member is signed in is only knowable from the
 * token in localStorage, which the server cannot see. `useAuthOrUndefined` rather than `useAuth`
 * because the latter throws when no AuthProvider is mounted, and this route must never hard-fail.
 *
 * `router.replace`, not `push`, so Back does not bounce the member into this redirector again.
 */
export default function PortalEntryPage() {
    const router = useRouter();
    const auth = useAuthOrUndefined();

    // undefined while the provider is still resolving the stored token — waiting avoids sending a
    // signed-in member to the login card for a frame.
    const isLoggedIn = auth?.isLoggedIn;

    useEffect(() => {
        if (isLoggedIn === undefined) return;
        router.replace(isLoggedIn ? URL_MEMBER_DASHBOARD : URL_AUTH_LOGIN);
    }, [isLoggedIn, router]);

    return (
        <div
            style={{
                minHeight: "100vh",
                background: T.page,
                fontFamily: T.fontFamily,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: T.muted,
                fontSize: 14,
            }}
        >
            <span role="status">Loading your membership portal…</span>
        </div>
    );
}
